import React, { useCallback, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import useAppNavigation from '~/hooks/useAppNavigation';

import CorporateEventsService from '~/services/CorporateEventsService';

import CompanyForm, { CreateCompanyParams } from '../components/CompanyForm';

export default function CompanyFormPage() {
  const { id } = useParams<{ id: string }>();
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!id;

  const { data: company, isLoading } = useQuery({
    queryKey: ['corporate-events', 'companies', id],
    queryFn: () => CorporateEventsService.getCompanyById(id),
    enabled: isEditMode,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyParams) => CorporateEventsService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'companies'] });
      enqueueSnackbar('Company created successfully', { variant: 'success' });
      push('/corporate-events/companies');
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating company: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCompanyParams> }) =>
      CorporateEventsService.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'companies'] });
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'companies', id] });
      enqueueSnackbar('Company updated successfully', { variant: 'success' });
      push('/corporate-events/companies');
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating company: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmit = useCallback(
    async (data: CreateCompanyParams) => {
      setIsSubmitting(true);
      try {
        if (isEditMode && id) {
          await updateMutation.mutateAsync({ id, data });
        } else {
          await createMutation.mutateAsync(data);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditMode, id, createMutation, updateMutation],
  );

  const handleCancel = () => {
    push('/corporate-events/companies');
  };

  if (isEditMode && isLoading) {
    return (
      <Container maxWidth="xl">
        <Spinner />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{isEditMode ? 'Edit Company' : 'Create Company'}</title>
      </Helmet>

      <PageHeader title={isEditMode ? 'Edit Company' : 'Create Company'} backButton="/corporate-events/companies" />

      <Box mt={4}>
        <CompanyForm
          company={company}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting || createMutation.isPending || updateMutation.isPending}
        />
      </Box>
    </Container>
  );
}
